import { MavLinkPacketParser, MavLinkProtocolV2, common, minimal, ardupilotmega } from '@m1/mavlink';
import useMountEffect from '@restart/hooks/useMountEffect';
import dayjs from 'dayjs';
import React, { createContext, useMemo, useRef } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { useLocation } from 'react-router-dom';

import actions from '@/actions';
import messageSet from '@/define/messageSet';
import EventEmitter from '@/libs/EventEmitter';
import * as sheco from '@/models/mavlink/px4-sheco/messages';

const MessageContext = createContext(null);
export { MessageContext };

const Provider = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const store = useStore();
  const mavlinkParser = useRef();
  const mavlinkProtocol = useRef();
  const mqttDJIWorker = useRef();
  const mqttEdgeWorker = useRef();
  const mqttViewerWorker = useRef();
  const robotMeta = useRef({});
  const registry = useRef({
    ...minimal.REGISTRY,
    ...common.REGISTRY,
    ...ardupilotmega.REGISTRY,
    ...sheco.REGISTRY,
  });

  const key = useMemo(() => {
    return location.pathname.split('/')[1];
  }, [location]);

  useMountEffect(() => {
    mavlinkParser.current = new MavLinkPacketParser();
    mavlinkProtocol.current = new MavLinkProtocolV2();

    // MQTT DJI Worker 정의
    mqttDJIWorker.current = new Worker('/workers/mqtt-dji-worker.js');
    // MQTT 연결
    mqttDJIWorker.current.postMessage({
      command: 'connect',
      data: {
        brokerUrl: `wss://${process.env.REACT_APP_MQTT_DJI_HOST}`,
        username: process.env.REACT_APP_MQTT_DJI_USERNAME,
        password: process.env.REACT_APP_MQTT_DJI_PASSWORD,
      },
    });

    // MQTT Edge Worker 정의
    mqttEdgeWorker.current = new Worker('/workers/mqtt-edge-worker.js');
    // MQTT 연결
    mqttEdgeWorker.current.postMessage({
      command: 'connect',
      data: {
        brokerUrl: process.env.REACT_APP_MQTT_EDGE_URL,
        username: process.env.REACT_APP_MQTT_EDGE_USERNAME,
        password: process.env.REACT_APP_MQTT_EDGE_PASSWORD,
      },
    });

    // MQTT Viewer Worker 정의
    mqttViewerWorker.current = new Worker('/workers/mqtt-viewer-worker.js');
    // MQTT 연결
    mqttViewerWorker.current.postMessage({
      command: 'connect',
      data: {
        brokerUrl: process.env.REACT_APP_MQTT_VIEWER_URL,
        username: process.env.REACT_APP_MQTT_VIEWER_USERNAME,
        password: process.env.REACT_APP_MQTT_VIEWER_PASSWORD,
      },
    });
    // 메시지 수신 처리
    mqttViewerWorker.current.onmessage = ({ data }) => messageHandler(data);

    return () => {
      if (mqttDJIWorker.current) {
        mqttDJIWorker.current.onmessage = null;
        mqttDJIWorker.current.terminate();
        mqttDJIWorker.current = null;
      }

      if (mqttEdgeWorker.current) {
        mqttEdgeWorker.current.onmessage = null;
        mqttEdgeWorker.current.terminate();
        mqttEdgeWorker.current = null;
      }

      if (mqttViewerWorker.current) {
        mqttViewerWorker.current.onmessage = null;
        mqttViewerWorker.current.terminate();
        mqttViewerWorker.current = null;
      }
    };
  });

  const messageHandler = ({ robotId, type, message }) => {
    // 텔레메트리 수신 전 로봇 메타 데이터 초기화되지 않은 상태 시
    if (!robotMeta.current[robotId]) return;

    const { isActive, isShow } = store.getState().robots.find(({ id }) => id === robotId);

    // 활성 로봇이 아닌 경우
    if (!isActive) {
      if (key === 'control-center') {
        dispatch(actions.robotOptions.init(robotId));
        dispatch(actions.robotParams.init(robotId));
      }
      dispatch(actions.telemetry.init(robotId));
      dispatch(actions.robotStatus.init(robotId));
      dispatch(actions.robots.activate(robotId));
    }
    // 노출 로봇이 아닌 경우
    else if (!isShow && key === 'mission-hub') return;

    switch (type) {
      case 'telemetry':
        switch (robotMeta.current[robotId].agentType) {
          case 'MavLink':
            handleMessageMavLink(robotId, message);
            break;

          case 'DJI':
            handleMessageDJI(robotId, message);
            break;

          default:
            break;
        }
        break;

      case 'iothub': // eg. M1 Tracker
        handleMessageIoTHub(robotId, message);
        break;

      default:
        break;
    }
  };

  // MavLink 메시지 처리
  const handleMessageMavLink = (robotId, message) => {
    const buffer = Buffer.from(message);
    const { header, payload } = JSON.parse(buffer.toString());

    // Component ID가 1이 아닌 경우 처리 제외
    if (header.compid !== 1) return;

    EventEmitter.publish(`${robotId}/telemetry/mavlink`, { header, payload });
  };

  // DJI 메시지 처리
  const handleMessageDJI = (robotId, message) => {
    const buffer = Buffer.from(message);
    const { header, payload } = JSON.parse(buffer.toString());

    EventEmitter.publish(`${robotId}/telemetry/dji`, { header, payload });
  };

  // IoTHub 메시지 처리
  const handleMessageIoTHub = (robotId, message) => {
    const { action, data } = JSON.parse(Buffer.from(message).toString());
    EventEmitter.publish(`${robotId}/event/${action}`, data);
  };

  const subscribeTelemetry = (robots) => {
    robots.forEach((robot) => {
      robotMeta.current[robot.id] = {
        commandSeq: 0,
        agentType: robot.model.agentType,
      };

      // 메시지 구독 요청
      mqttViewerWorker.current.postMessage({
        command: 'subscribe',
        data: {
          topics: [
            `robots/${robot.id}/telemetry`, // 텔레메트리
            `robots/${robot.id}/iothub`, // IoTHub
          ],
        },
      });
    });
  };

  const publishCommand = (robot, name, data) => {
    // 텔레메트리 수신 전 로봇 메타 데이터 초기화되지 않은 상태 시
    if (!robotMeta.current[robot.id]) return;

    switch (robotMeta.current[robot.id].agentType) {
      case 'MavLink':
        publishMavLinkCommand(robot, name, data);
        break;

      case 'DJI':
        publishDJICommand(robot, name, data);
        break;

      default:
        break;
    }
  };

  const publishMavLinkCommand = (robot, name, paramsSet) => {
    const { sysId, compId, messages } = messageSet.mavlink[name];
    const protocol = new MavLinkProtocolV2(sysId, compId);

    for (let messageIndex = 0; messageIndex < messages.length; messageIndex++) {
      const message = messages[messageIndex];
      const { isMultiple, msgId, fields } = message;
      const paramsGroup = [];

      // 반복 메시지 경우
      if (isMultiple) {
        paramsSet.shift().forEach((params) => paramsGroup.push(params));
      }
      // 단일 메시지 경우
      else {
        paramsGroup.push(paramsSet.shift());
      }

      for (let paramsIndex = 0; paramsIndex < paramsGroup.length; paramsIndex++) {
        const params = paramsGroup[paramsIndex];
        const mavMessage = new registry.current[msgId]();
        const mavMessageFields = registry.current[msgId].FIELDS;

        fields.forEach((field) => {
          const { name } = mavMessageFields.find(({ source }) => source === field.name);

          // 변수인 경우
          if (field.isVariable) {
            mavMessage[name] = params.shift();
          } else {
            mavMessage[name] = field.value;
          }
        });

        const seq = robotMeta.current[robot.id].commandSeq;
        robotMeta.current[robot.id].commandSeq = (seq + 1) % 256;

        let message = protocol.serialize(mavMessage, seq);

        //#region M1Edge 통신 단절 중 발송된 메시지의 지연된 작용 방지를 위한 처리
        const timestamp = dayjs().valueOf();
        const timestampBuffer = Buffer.alloc(8);
        timestampBuffer.writeBigUInt64BE(BigInt(timestamp * 1000));
        message = Buffer.concat([timestampBuffer, message]);
        //#endregion

        mqttEdgeWorker.current.postMessage({
          command: 'publish',
          data: {
            topic: `robots/${robot.id}/command/air`,
            message,
          },
        });
      }
    }
  };

  const publishDJICommand = (robot, name, params) => {
    const { topic: topicRaw, fields } = messageSet.dji[name];

    // topic의 placeholder(:DJI_REMOTE_CONTROLLER_SN 등)를 robot.metadata의 실제 값으로 치환
    const topic = topicRaw.replace(/:(\w+)/g, (_match, key) => {
      return robot.metadata[key];
    });

    const message = {};
    fields.forEach((field) => {
      const value = field.isVariable ? params.shift() : field.value;

      // 중첩된 객체 처리 (예: "data.user_id" → message.data.user_id)
      const keys = field.name.split('.');
      let current = message;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) {
          current[keys[i]] = {};
        }
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
    });

    mqttDJIWorker.current.postMessage({
      command: 'publish',
      data: {
        topic,
        message: JSON.stringify(message),
      },
    });
  };

  const publishEvent = (robot, event, data) => {
    mqttEdgeWorker.current.postMessage({
      command: 'publish',
      data: {
        topic: `robots/${robot.id}/event`,
        message: JSON.stringify({ event, data }),
      },
    });
  };

  return (
    <MessageContext.Provider value={{ subscribeTelemetry, publishCommand, publishEvent }}>
      {children}
    </MessageContext.Provider>
  );
};

export default Provider;
